import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { OrcDataset } from 'app/model/dataset/orc.dataset';
import { HierarchicalNode, NamedArray, Column } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(OrcDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                orcCompressionCodec: '*',
                enablePhysicalSchema: '*'
            }
        }
    }
})
export class OrcDatasetAdapter extends FormatDatasetAdapter<OrcDataset, Model.DatasetV2Resource> {
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        try {
            return parseOrcSchema(obj);
        } catch {
            return undefined;
        }
    }

    protected async _toResource(dataset: OrcDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.OrcDataset;
        modelDataset.type = Metamodel.modelFor(OrcDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {};
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }

        modelDataset.typeProperties.orcCompressionCodec = ValueAdapter.toResource(dataset, OrcDataset.COMPRESSION_CODEC);

        modelDataset.typeProperties.enablePhysicalSchema = dataset.enablePhysicalSchema;

        modelDataset.schema = dataset.enablePhysicalSchema ? dataset.rawHierarchicalSchema : this.columnValueToResource(dataset, dataset.columns);

        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: OrcDataset): Promise<OrcDataset> {
        if (!dataset) {
            dataset = OrcDataset.MODEL.create<OrcDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        const props = modelDatasetResource.properties as Model.OrcDataset;

        if (props.typeProperties != null) {
            ValueAdapter.toModel(dataset, OrcDataset.COMPRESSION_CODEC, props.typeProperties.orcCompressionCodec);
        }

        dataset.enablePhysicalSchema = undefined;
        dataset.enablePhysicalSchema = props.typeProperties.enablePhysicalSchema;
        if (dataset.enablePhysicalSchema) {
            dataset.rawHierarchicalSchema = props.schema;
            dataset.schemas = this.parseHierarchicalSchema(dataset.rawHierarchicalSchema);
            dataset.columns = dataset.schemas.children.map((c) => {
                const col = new Column();
                col.name = c.name;
                col.physicalType = c.columnType;
                col.columnType = c.columnType;
                return col;
            }) as NamedArray<Column>;
        } else {
            dataset.columns = this.columnResourceToValue(dataset, modelDatasetResource.properties.schema);
        }

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, OrcDataset.MODEL));
        return dataset;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseOrcSchema(schema: any): HierarchicalNode {
    const root = new HierarchicalNode();
    if (schema != null && schema.length > 0) {
        root.name = 'root';
        root.columnType = 'object';
        root.kind = 'object';

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parseNode = (curNode: HierarchicalNode, obj: any): void => {
            curNode.name = obj.name;

            // eslint-disable-next-line
            if (typeof obj.type === 'string') {
                // the type is primitive type
                curNode.columnType = obj.type;
            } else if (Array.isArray(obj.type)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const typeObj = (obj.type as any[]).find((t) => typeof t === 'object');
                // parse union of object, can only contain at most 1 object
                if (typeObj != null) {
                    curNode.columnType = typeObj.type;
                    obj = typeObj;
                } else {
                    curNode.columnType = (obj.type as []).map((t) => typeof t === 'string' ? t : 'any').join(' | ');
                }
            } else {
                // the type is complex type
                curNode.columnType = obj.type.type;
                obj = obj.type;
            }

            if (curNode.columnType === 'DECIMAL') {
                curNode.precision = obj.precision;
                curNode.scale = obj.scale;
            } else if (curNode.columnType === 'LIST') {
                curNode.properties = {};
                const items = new HierarchicalNode();
                curNode.items = items;
                curNode.kind = 'array';
                parseNode(items, obj.items);
            } else if (curNode.columnType === 'MAP') {
                curNode.kind = 'object';
                curNode.properties = {};
                const keyType = new HierarchicalNode();
                curNode.properties['keyType'] = keyType;
                parseNode(keyType, { name: 'keyType', ...obj['key'] });
                const valueType = new HierarchicalNode();
                curNode.properties['valueType'] = valueType;
                parseNode(valueType, { name: 'valueType', ...obj['value'] });
            } else if (curNode.columnType === 'STRUCT') {
                curNode.kind = 'object';
                curNode.properties = {};
                const keys = Object.keys(obj.properties);
                keys.forEach((key) => {
                    const node = new HierarchicalNode();
                    curNode.properties[key] = node;
                    parseNode(node, { name: key, ...obj.properties[key] });
                });
            } else if (curNode.columnType === 'UNION') {
                curNode.kind = 'object';
                curNode.properties = {};
                if (Array.isArray(obj.items) && obj.items.length > 0) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (obj.items as any[]).forEach((item, idx) => {
                        const node = new HierarchicalNode();
                        curNode.properties[`item_${idx}`] = node;
                        parseNode(node, { name: `item_${idx}`, ...item });
                    });
                }
            } else if (curNode.columnType === 'DECIMAL') {
                curNode.precision = obj.precision;
                curNode.scale = obj.scale;
            }
        };

        root.properties = {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (schema as any[]).forEach((obj) => {
            const node = new HierarchicalNode();
            root.properties[obj.name] = node;
            parseNode(node, obj);
        });
    }

    return root;
}
